import { isDefined } from "~/shared/common";
import type { AgentSchedule, AgentScheduleUpdate } from "~/shared/types";
import { makeDuid, makeEmptyLexicalState } from "~/utils/common";
import { agentScheduleComparator } from "~/utils/comparator";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { removeFromSorted, updateInSorted } from "./utils";

export type State = {
  _duidsToAgentSchedules: Map<string, AgentSchedule>;
  _sortedAgentScheduleDuids: string[];
};

export const state: State = {
  _duidsToAgentSchedules: new Map(),
  _sortedAgentScheduleDuids: [],
};

export type Getters = {
  getAgentSchedulesByAgentDuidOrdered: (agentDuid: string) => AgentSchedule[];
  getAgentScheduleByDuid: (duid: string) => AgentSchedule | undefined;
};

export type Actions = {
  createAgentSchedule: (
    agentDuid: string,
    partialSchedule?: Partial<AgentSchedule>,
    options?: { awaitBackend?: boolean }
  ) => Promise<AgentSchedule>;
  updateAgentSchedule: (
    update: AgentScheduleUpdate,
    options?: { awaitBackend?: boolean }
  ) => Promise<AgentSchedule | undefined>;
  deleteAgentSchedule: (schedule: AgentSchedule, options?: { awaitBackend?: boolean }) => Promise<void>;
  $createOrUpdateAgentSchedule: (schedule: AgentSchedule) => void;
  $deleteAgentSchedule: (schedule: AgentSchedule) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  getAgentSchedulesByAgentDuidOrdered() {
    return (agentDuid) =>
      this._sortedAgentScheduleDuids
        .map((e) => this._duidsToAgentSchedules.get(e))
        .filter(isDefined)
        .filter((e) => e.agentDuid === agentDuid);
  },
  getAgentScheduleByDuid() {
    return (duid) => this._duidsToAgentSchedules.get(duid);
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async createAgentSchedule(agentDuid, partialSchedule = {}, options = {}) {
    const { awaitBackend = false } = options;
    const now = new Date().toISOString();
    const schedule: AgentSchedule = {
      duid: makeDuid(),
      createdAt: now,
      updatedAt: now,
      agentDuid,
      enabled: true,
      title: "",
      prompt: makeEmptyLexicalState(),
      recurrence: null,
      recursNextAt: null,
      ...partialSchedule,
    };

    this.$createOrUpdateAgentSchedule(schedule);

    const backendAction = this.$backend.agentSchedule.create(schedule);
    if (awaitBackend) {
      await backendAction;
    }
    return schedule;
  },
  async updateAgentSchedule(update, options = {}) {
    const { awaitBackend = false } = options;
    const schedule = this.getAgentScheduleByDuid(update.duid);
    if (!schedule) {
      return undefined;
    }

    Object.assign(schedule, update);

    const backendAction = this.$backend.agentSchedule.update(update);
    if (awaitBackend) {
      await backendAction;
    }
    return schedule;
  },
  async deleteAgentSchedule(schedule, options = {}) {
    const { awaitBackend = false } = options;

    this.$deleteAgentSchedule(schedule);

    const backendAction = this.$backend.agentSchedule.delete(schedule.duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  $createOrUpdateAgentSchedule(schedule) {
    const currentSchedule = this.getAgentScheduleByDuid(schedule.duid);

    if (!currentSchedule) {
      this._duidsToAgentSchedules.set(schedule.duid, schedule);
    } else {
      Object.assign(currentSchedule, schedule);
    }
    updateInSorted(this._duidsToAgentSchedules, this._sortedAgentScheduleDuids, schedule.duid, agentScheduleComparator);
  },
  $deleteAgentSchedule(schedule) {
    this._duidsToAgentSchedules.delete(schedule.duid);
    removeFromSorted(this._sortedAgentScheduleDuids, schedule.duid);
  },
};

export { actions, getters };
